import React from "react";
import { ArrowUpRight } from "lucide-react";
import ThreeCanvas from "./ThreeCanvas";

export default function Lookbook() {
  const images = {
    hero_model_1: "https://images.pexels.com/photos/11317811/pexels-photo-11317811.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    lookbook_model_vibrant: "https://images.unsplash.com/photo-1721637686340-de9f8cebda5a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxzdHJlZXR3ZWFyJTIwbW9kZWwlMjBmYXNoaW9ufGVufDB8fHx8MTc4Mjg1MjA4M3ww&ixlib=rb-4.1.0&q=85",
    lookbook_model_minimal: "https://images.unsplash.com/photo-1760782328680-3a20186d5427?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwyfHxtaW5pbWFsaXN0JTIwdXJiYW4lMjBjbG90aGluZ3xlbnwwfHx8fDE3ODI4NTIwODN8MA&ixlib=rb-4.1.0&q=85",
    texture_detail: "https://images.pexels.com/photos/28504904/pexels-photo-28504904.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
  };

  return (
    <section id="lookbook" className="py-24 bg-[#F4F4F2] border-t border-border_subtle text-text_primary relative overflow-hidden font-body">
      {/* Editorial Slow Background Marquee */}
      <div className="absolute top-10 left-0 w-full overflow-hidden pointer-events-none opacity-5">
        <div className="animate-marquee-slow whitespace-nowrap flex font-heading text-[12vw] italic font-semibold uppercase leading-none">
          WOVLL EDITION 2026 // SWISS MINIMAL LUXURY // HIGH CONTRAST STREETWEAR //&nbsp;
          WOVLL EDITION 2026 // SWISS MINIMAL LUXURY // HIGH CONTRAST STREETWEAR //&nbsp;
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Section Heading */}
        <div className="mb-16 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <span className="font-mono-accent text-xs uppercase tracking-widest text-[#4F46E5] block mb-2">
              LOOKBOOK // EDITORIAL 01
            </span>
            <h2 className="font-heading italic text-5xl md:text-7xl font-semibold leading-tight tracking-tighter">
              El Arte del Contraste
            </h2>
          </div>
          <p className="max-w-md text-text_secondary font-light leading-relaxed text-sm md:text-base">
            Inspirado en la tensión entre la precisión geométrica suiza y la libertad fluida de la cultura urbana moderna. Una exploración del espacio negativo.
          </p>
        </div>

        {/* Asymmetric Bento Tetris Grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          {/* Cell 1: Large Photography - 7 Cols */}
          <div 
            data-testid="lookbook-bento-1"
            className="md:col-span-7 bg-white p-4 border border-border_subtle flex flex-col justify-between group cursor-pointer hover:border-[#0A0A0A] transition-colors"
          >
            <div className="overflow-hidden relative aspect-[4/3] w-full bg-[#F4F4F2]">
              <img
                src={images.lookbook_model_vibrant}
                alt="Streetwear Vibrant Neon"
                className="w-full h-full object-cover group-hover:scale-95 transition-transform duration-700 ease-out"
              />
              <div className="absolute top-4 right-4 bg-white px-3 py-1.5 font-mono-accent text-[9px] uppercase tracking-widest border border-border_subtle">
                ARTISTIC VIBRANCY
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between">
              <div>
                <span className="font-mono-accent text-[10px] tracking-widest text-text_secondary">CRAFTED IN TOKYO // ZURICH</span>
                <h3 className="font-heading italic text-2xl font-semibold mt-1">Sinfonía Urbana Neón</h3>
              </div>
              <div className="w-10 h-10 border border-border_subtle flex items-center justify-center group-hover:bg-[#0A0A0A] group-hover:text-white transition-colors">
                <ArrowUpRight className="w-4 h-4" />
              </div>
            </div>
          </div>

          {/* Cell 2: Interactive 3D Canvas Windows - 5 Cols */}
          <div 
            data-testid="lookbook-bento-2"
            className="md:col-span-5 bg-[#0A0A0A] text-white p-6 flex flex-col justify-between relative group overflow-hidden"
          >
            {/* Background 3D Canvas preview */}
            <div className="absolute inset-0 opacity-40 pointer-events-none group-hover:opacity-75 transition-opacity duration-300">
              <ThreeCanvas shapeType="torusKnot" color="#FF3B30" />
            </div>

            <div className="relative z-10 flex justify-between items-start">
              <span className="font-mono-accent text-[10px] uppercase tracking-widest text-[#FF3B30]">
                interactive 3d canvas
              </span>
              <span className="font-mono-accent text-[10px] uppercase tracking-widest text-white/40">
                02 // 05
              </span>
            </div>

            <div className="relative z-10 mt-36">
              <h3 className="font-heading italic text-4xl leading-none font-semibold mb-3">
                Geometría en Movimiento
              </h3>
              <p className="text-xs text-white/60 font-light leading-relaxed max-w-xs">
                Mueve tu cursor por la pantalla para distorsionar la gravedad tridimensional del tejido modular Wovll.
              </p>
            </div>
          </div>

          {/* Cell 3: Small Texture/Detail - 4 Cols */}
          <div 
            data-testid="lookbook-bento-3"
            className="md:col-span-4 bg-white p-4 border border-border_subtle flex flex-col justify-between group cursor-pointer hover:border-[#0A0A0A] transition-colors"
          >
            <div className="overflow-hidden relative aspect-square w-full bg-[#F4F4F2]">
              <img
                src={images.texture_detail}
                alt="Textile Detail"
                className="w-full h-full object-cover group-hover:scale-95 transition-transform duration-700 ease-out"
              />
              <div className="absolute bottom-4 left-4 bg-white px-3 py-1.5 font-mono-accent text-[9px] uppercase tracking-widest border border-border_subtle">
                TEXTURE NO. 09
              </div>
            </div>
            <div className="mt-4">
              <span className="font-mono-accent text-[10px] tracking-widest text-text_secondary">COTTON 320GSM</span>
              <h3 className="font-heading italic text-xl font-semibold mt-1">El Tacto del Lujo</h3>
            </div>
          </div>

          {/* Cell 4: Medium Portrait Model - 8 Cols */}
          <div 
            data-testid="lookbook-bento-4"
            className="md:col-span-8 bg-white p-4 border border-border_subtle flex flex-col md:flex-row gap-6 justify-between group cursor-pointer hover:border-[#0A0A0A] transition-colors"
          >
            <div className="overflow-hidden relative md:w-1/2 aspect-[4/5] bg-[#F4F4F2]">
              <img
                src={images.lookbook_model_minimal}
                alt="Streetwear Minimal Gray"
                className="w-full h-full object-cover group-hover:scale-95 transition-transform duration-700 ease-out"
              />
            </div>
            <div className="md:w-1/2 flex flex-col justify-between py-2">
              <div>
                <span className="font-mono-accent text-[10px] tracking-widest text-[#4F46E5] uppercase">Minimalist luxury</span>
                <h3 className="font-heading italic text-3xl font-semibold mt-2 mb-4 leading-tight">
                  Elegancia Monocromática
                </h3>
                <p className="text-sm text-text_secondary font-light leading-relaxed">
                  Siluetas sobrias diseñadas para mimetizarse con la arquitectura brutalista. Algodón orgánico premium con cortes arquitectónicos que desafían la gravedad.
                </p>
              </div>
              <div className="flex items-center justify-between pt-6 border-t border-border_subtle font-mono-accent text-xs">
                <span className="tracking-widest">DISCOVER CUTS // DETALLES</span>
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
